'use client'

import { useEffect, useState } from 'react'

export const REMOVING_BACKGROUND_MESSAGE = "Hang tight, we're removing the background"

/**
 * The second-stage message, shown once the wait stops looking routine.
 *
 * Not invented: this is `CreationToolStatus.title(ConvertingToSticker)`
 * (packages/creation/src/creation-tools/types/status.ts:42), the copy the
 * upload list already shows while converting a source to a sticker — the same
 * operation, under a different name. It is the only playful waiting line in the
 * codebase, so reusing it keeps one voice rather than adding a second.
 */
export const REMOVING_BACKGROUND_MESSAGE_SLOW = 'Casting Sticker Spell'

/**
 * How long the first message stands before the wait counts as slow.
 *
 * A still image is one request and lands well inside this. An animated source
 * is one request per third of a second of footage, so anything of length is
 * always going to cross it — which is the point: the change is what tells the
 * user the page is alive and still working, not stuck.
 */
export const SLOW_MESSAGE_DELAY_MS = 10_000

/**
 * The message to show while background removal runs.
 *
 * `isActive` guards the timer so the still-image tool UI, which never shows
 * either message, does not schedule one or re-render when it fires.
 */
export const useRemovingBackgroundMessage = (isActive: boolean): string => {
    const [isSlow, setIsSlow] = useState(false)

    useEffect(() => {
        if (!isActive) {
            setIsSlow(false)
            return
        }

        const timer = setTimeout(() => setIsSlow(true), SLOW_MESSAGE_DELAY_MS)
        return () => clearTimeout(timer)
    }, [isActive])

    return isSlow ? REMOVING_BACKGROUND_MESSAGE_SLOW : REMOVING_BACKGROUND_MESSAGE
}
